import React, { useCallback, useState } from 'react';
import { FORM_ERROR } from 'final-form';

import {
  Link,
  RouteComponentProps,
  WindowLocation,
  useLocation,
} from '@gatsbyjs/reach-router';
import { Auth } from '~/amplify';
import { Helmet as MetaTags } from 'react-helmet';
import { navigate } from '@gatsbyjs/reach-router';
import { Form, Field } from '~/components/organism/Forms';
import PasswordInputGroup from '~/components/page/Auth/components/PasswordInputGroup';
import Catalog from '~/Catalog';
import FormUtils from '~/components/organism/FormUtils';
import Button from '~/components/atom/Button';
import TEST_ID from './index.testid';
import { Body, Heading1, Heading3 } from '~/components/atom/Typography';
import JustificationContainer from '~/components/atom/JustificationContainer';
import AuthFormWrapper from '../components/AuthFormWrapper';
import usePasswordValidation from '~/hooks/usePasswordValidation';
import Input from '~/components/molecule/Input';
import createPageTitle from '~/util/createPageTitle';
import { reporter } from '~/hooks/useErrorReporter';

type FormData = {
  verificationCode: string | null;
  password: string | null;
  passwordRepeat: string | null;
};

type FormErrors = Partial<Record<keyof FormData, string>>;

type Props = RouteComponentProps<{}>;

type WindowState = {
  email?: string;
  code?: string;
};

type Credentials = {
  email: string | null;
  code: string | null;
};

const text = {
  title: 'Maak nieuw wachtwoord',
  verificationCodeLabel: 'Verificatienummer',
  noVerificationCode: 'Voer verificatienummer in',
  submitButton: 'Maak nieuw wachtwoord aan',
  successMessage: 'Jouw wachtwoord is aangepast. Je kan nu inloggen!',
  codeErrorMessage: 'Het verificatienummer klopt niet. Probeer het nog eens.',
  noAccountText: 'Nog geen account?',
  registerLinkText: 'Registreer je hier',
  emailSentText:
    'Er is een mail verstuurd naar {email} met een verificatienummer erin.',
};

const SetupPassword: React.FCC<Props> = () => {
  const location = useLocation() as WindowLocation<WindowState>;
  const initialCredentials = getParamsFromMagicLink(location);

  const { validatePassword, passwordConditions } = usePasswordValidation();

  const [isFocused] = useState<boolean>(
    Boolean(initialCredentials.email && initialCredentials.code),
  );

  const hasError = !initialCredentials.email;

  const validate = useCallback(
    ({ verificationCode, password, passwordRepeat }: FormData): FormErrors => {
      const errors: FormErrors = {};

      if (!verificationCode) {
        errors.verificationCode = text.noVerificationCode;
      }

      const passwordErrors = validatePassword({ password, passwordRepeat });

      return { ...errors, ...passwordErrors };
    },
    [validatePassword],
  );

  const onFormSubmit = async ({
    verificationCode,
    password,
  }: FormData): Promise<{ [FORM_ERROR]: string } | void> => {
    const email = initialCredentials.email || location?.state?.email;

    if (!email || !verificationCode || !password) return;

    try {
      await Auth.confirmResetPassword({
        username: email,
        confirmationCode: verificationCode,
        newPassword: password,
      });

      void navigate('/login', {
        state: {
          message: text.successMessage,
        },
      });
    } catch (error: unknown) {
      return handleResetPasswordError(error);
    }
  };

  const initialValues: FormData = {
    verificationCode: initialCredentials.code,
    password: null,
    passwordRepeat: null,
  };

  if (hasError) {
    return (
      <>
        <MetaTags>
          <title>{createPageTitle(text.title)}</title>
        </MetaTags>

        <AuthFormWrapper>
          <Heading1
            margin={['l', null]}
            lineHeight="base"
            color={{ group: 'accent' }}
            size="xxxl"
            skewed
          >
            {text.title}
          </Heading1>

          <Heading3 fontWeight="regular">
            <Body
              size="base"
              color={{ group: 'danger', variant: 'light' }}
              margin={[null]}
              data-testid={TEST_ID.INITIAL_ERROR_MESSAGE}
            >
              {Catalog.genericUnknownErrorMessage}
            </Body>
          </Heading3>
        </AuthFormWrapper>
      </>
    );
  }

  return (
    <>
      <MetaTags>
        <title>{createPageTitle(text.title)}</title>
      </MetaTags>

      <AuthFormWrapper>
        <Heading1
          margin={['l', null]}
          lineHeight="base"
          color={{ group: 'accent' }}
          size="xxxl"
          skewed
        >
          {text.title}
        </Heading1>

        {location?.state?.email && (
          <p>{text.emailSentText.replace('{email}', location.state.email)}</p>
        )}

        <Form
          onSubmit={onFormSubmit}
          validate={validate}
          initialValues={initialValues}
        >
          {({
            handleSubmit,
            submitError,
            submitting,
            pristine,
            hasValidationErrors,
          }) => (
            <form
              id="setupPassword"
              onSubmit={handleSubmit}
              data-testid={TEST_ID.SETUP_PASSWORD_FORM}
            >
              {submitError ? (
                <Body
                  size="base"
                  color={{ group: 'danger', variant: 'light' }}
                  margin={[null]}
                  data-testid={TEST_ID.SETUP_PASSWORD_ERROR_MESSAGE}
                >
                  {submitError}
                </Body>
              ) : null}

              <JustificationContainer margin={['m', null]}>
                <Field name="verificationCode">
                  {({ input, meta: { error, touched } }) => (
                    <Input
                      {...input}
                      size="large"
                      width="100%"
                      label={{
                        text: text.verificationCodeLabel,
                        color: { group: 'white' },
                      }}
                      type="text"
                      externalErrors={
                        error && touched
                          ? [FormUtils.showError(error, touched)]
                          : undefined
                      }
                      disabled={submitting}
                    />
                  )}
                </Field>
              </JustificationContainer>

              <PasswordInputGroup
                labelColor={{ group: 'white' }}
                submitting={submitting}
                passwordConditions={passwordConditions}
                isFocused={isFocused}
              />

              <JustificationContainer align="center" justification="end">
                <Button
                  size="large"
                  appearance="secondary"
                  type="submit"
                  disabled={submitting || pristine || hasValidationErrors}
                  data-testid={TEST_ID.SUBMIT_PASSWORD_BUTTON}
                  label={text.submitButton}
                />
              </JustificationContainer>
            </form>
          )}
        </Form>

        <Body
          align="center"
          size="base"
          margin={['xl', null, null, null]}
          color={{ group: 'white' }}
        >
          {text.noAccountText}{' '}
          <Link to={`/register${location?.search}`}>
            {text.registerLinkText}
          </Link>
        </Body>
      </AuthFormWrapper>
    </>
  );
};

const getParamsFromMagicLink = (
  location: WindowLocation<WindowState>,
): Credentials => {
  if (location.state && location.state.email) {
    return {
      email: location.state.email,
      code: location.state.code || null,
    };
  }
  const searchString = location.search;

  const email = searchString.match(new RegExp(/email=.*(?=&code=)/, 'g'));
  const code = searchString.match(new RegExp(/code=.*$/, 'g'));

  return {
    email: email ? email[0].slice(6) : null,
    code: code ? code[0].slice(5) : null,
  };
};

const handleResetPasswordError = (error: unknown): { [FORM_ERROR]: string } => {
  const err = error as any;

  if (
    err.name === 'CodeMismatchException' ||
    err.code === 'CodeMismatchException'
  ) {
    return {
      [FORM_ERROR]: err.message || text.codeErrorMessage,
    };
  }

  reporter.captureException(err);
  return {
    [FORM_ERROR]: err.message || Catalog.genericUnknownErrorMessage,
  };
};

export default SetupPassword;
